import { useCallback, useEffect, useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { Button } from "../../components/Button";
import { Hero } from "../../components/Hero";
import { I } from "../../components/icons";
import { Notice } from "../../components/Notice";
import { NavRow, RowGroup } from "../../components/Rows";
import { Screen } from "../../components/Shell";
import { Tag } from "../../components/Tag";
import { TopBar } from "../../components/TopBar";
import { useIsAdmin, useKaya } from "../../store/kayaStore";
import { api, type AdminUser, type UserStatus } from "../../sync/api";
import { UserViewNotice } from "./UserViewNotice";
import "../You.css";

function agoText(ms: number | null | undefined): string {
  if (!ms) return "never";
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 24 * 60) return `${Math.floor(mins / 60)} h ago`;
  const d = Math.floor(mins / (24 * 60));
  if (d === 1) return "yesterday";
  if (d < 30) return `${d} days ago`;
  return new Date(ms).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

function asked(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
  const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `Asked ${day}, ${time}`;
}

const firstName = (u: AdminUser) => (u.name || u.email).split(/[\s@]/)[0];

/**
 * People: the roster, answering the one question an admin opens it for — is anybody waiting?
 *
 * Whoever is waiting is the hero, with the two buttons that resolve it, because an approval queue
 * that needs a tap to reach is a queue people forget to look at. Everyone else is a row that opens
 * their records.
 */
export default function AdminUsers() {
  const { user, showToast } = useKaya();
  const isAdmin = useIsAdmin();
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers((await api.adminUsers()).users);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load the list");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(u: AdminUser, status: UserStatus) {
    setBusy(u.id);
    try {
      await api.adminPatchUser(u.id, { status });
      showToast(status === "approved" ? `${u.name || u.email} is in` : "Access removed");
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(null);
    }
  }

  const bySeen = (a: AdminUser, b: AdminUser) => (b.lastSyncAt ?? 0) - (a.lastSyncAt ?? 0);
  const pending = users?.filter((u) => u.status === "pending") ?? [];
  const active = (users?.filter((u) => u.status === "approved") ?? []).sort(bySeen);
  const closed = (users?.filter((u) => u.status === "suspended") ?? []).sort(bySeen);
  const [first, ...rest] = pending;

  const decide = (u: AdminUser, grow?: boolean) => (
    <>
      <Button variant="ghost" disabled={busy === u.id} onClick={() => void setStatus(u, "suspended")}>
        Refuse
      </Button>
      <Button
        grow={grow}
        icon={I.check}
        busy={busy === u.id}
        onClick={() => void setStatus(u, "approved")}
        ariaLabel={`Approve ${u.name || u.email}`}
      >
        {grow ? `Approve ${firstName(u)}` : "Approve"}
      </Button>
    </>
  );

  // One IonPage for this route, whatever state it is in — see UserViewNotice for what swapping
  // the page element out from under the router outlet does.
  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="People" back={{ to: "/you", label: "Back to You" }} />
          <div className="you-page">
            {!isAdmin ? <UserViewNotice /> : null}

            {isAdmin && err ? (
              <Notice tone="danger" primary={{ label: "Try again", onClick: () => void load() }}>
                {err}
              </Notice>
            ) : null}

            {isAdmin && !err ? (
              first ? (
                <Hero
                  ariaLabel="Waiting for approval"
                  label="Waiting for approval"
                  figure={pending.length}
                  unit="waiting"
                  line={`${first.name || "No name given"} · ${first.email}`}
                  sub={asked(first.createdAt)}
                  actions={decide(first, true)}
                />
              ) : (
                <Hero
                  label="Waiting for approval"
                  title={users ? "Nobody is waiting" : "Loading…"}
                  line={
                    users
                      ? `${active.length} ${active.length === 1 ? "person has" : "people have"} access. Approving opens the whole app; nothing logged before is affected.`
                      : undefined
                  }
                />
              )
            ) : null}

            {isAdmin && rest.length ? (
              <RowGroup heading="Also waiting">
                {rest.map((u) => (
                  <NavRow
                    key={u.id}
                    kind="action"
                    title={u.name || "No name given"}
                    subtitle={`${u.email} · ${asked(u.createdAt).toLowerCase()}`}
                    action={<div className="you-actions">{decide(u)}</div>}
                  />
                ))}
              </RowGroup>
            ) : null}

            {isAdmin && active.length ? (
              <RowGroup heading={`Approved · ${active.length}`}>
                {active.map((u) => (
                  <NavRow
                    key={u.id}
                    title={`${u.name || u.email}${u.id === user?.id ? " (you)" : ""}`}
                    subtitle={`${u.email}${u.role === "admin" ? " · admin" : ""} · last synced ${agoText(u.lastSyncAt)}`}
                    value={<Tag tone="measured">approved</Tag>}
                    to={`/you/people/${u.id}`}
                  />
                ))}
              </RowGroup>
            ) : null}

            {isAdmin && closed.length ? (
              <RowGroup heading={`Suspended · ${closed.length}`}>
                {closed.map((u) => (
                  <NavRow
                    key={u.id}
                    title={u.name || u.email}
                    subtitle={`${u.email} · last synced ${agoText(u.lastSyncAt)}`}
                    value={<Tag tone="est">suspended</Tag>}
                    to={`/you/people/${u.id}`}
                  />
                ))}
              </RowGroup>
            ) : null}

            {isAdmin ? (
              <p className="you-fine">
                A suspended account keeps its data and can be approved again. Everything here is
                somebody else&rsquo;s record of their own body. Admin is set by ADMIN_EMAILS on the
                server, not from this screen.
              </p>
            ) : null}
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
